import React from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';

interface SlideSettingsProps {
  tempColumns: number;
  slideWidth: number;
  slideWidthInput: string;
  viewportWidth?: number;
  viewportWidthInput: string;
  positionAdjustment: number;
  positionAdjustmentInput: string;
  initialIndex: number;
  indicatorPosition: 'top' | 'bottom';
  infiniteLoop: boolean;
  horizontalSlidesLength: number;
  indicatorOffsetX: number;
  indicatorOffsetXInput: string;
  indicatorOffsetY: number;
  indicatorOffsetYInput: string;
  onTempColumnsChange: (value: number) => void;
  onApplyColumns: () => void;
  onSlideWidthChange: (value: number) => void;
  onSlideWidthInputChange: (value: string) => void;
  onSlideWidthBlur: (value: string) => void;
  onViewportWidthChange: (value: number | undefined) => void;
  onViewportWidthInputChange: (value: string) => void;
  onViewportWidthBlur: (value: string) => void;
  onPositionAdjustmentChange: (value: number) => void;
  onPositionAdjustmentInputChange: (value: string) => void;
  onPositionAdjustmentBlur: () => void;
  onInitialIndexChange: (value: number) => void;
  onIndicatorPositionChange: (value: 'top' | 'bottom') => void;
  onInfiniteLoopChange: (value: boolean) => void;
  onIndicatorOffsetXChange: (value: number) => void;
  onIndicatorOffsetXInputChange: (value: string) => void;
  onIndicatorOffsetXBlur: () => void;
  onIndicatorOffsetYChange: (value: number) => void;
  onIndicatorOffsetYInputChange: (value: string) => void;
  onIndicatorOffsetYBlur: () => void;
}

export const SlideSettings: React.FC<SlideSettingsProps> = ({
  tempColumns,
  slideWidth,
  slideWidthInput,
  viewportWidth,
  viewportWidthInput,
  positionAdjustment,
  positionAdjustmentInput,
  initialIndex,
  indicatorPosition,
  infiniteLoop,
  horizontalSlidesLength,
  indicatorOffsetX,
  indicatorOffsetXInput,
  indicatorOffsetY,
  indicatorOffsetYInput,
  onTempColumnsChange,
  onApplyColumns,
  onSlideWidthChange,
  onSlideWidthInputChange,
  onSlideWidthBlur,
  onViewportWidthChange,
  onViewportWidthInputChange,
  onViewportWidthBlur,
  onPositionAdjustmentChange,
  onPositionAdjustmentInputChange,
  onPositionAdjustmentBlur,
  onInitialIndexChange,
  onIndicatorPositionChange,
  onInfiniteLoopChange,
  onIndicatorOffsetXChange,
  onIndicatorOffsetXInputChange,
  onIndicatorOffsetXBlur,
  onIndicatorOffsetYChange,
  onIndicatorOffsetYInputChange,
  onIndicatorOffsetYBlur,
}) => {
  return (
    <>
      {/* 列数設定 */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000' }}>
            列数
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#666666' }}>
            1列固定で、最大10列まで設定可能
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
            <TextField
              type="number"
              value={tempColumns}
              onChange={(e) => onTempColumnsChange(parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: 10 }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                '& fieldset': {
                  borderColor: '#D0D0D0',
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                  '&::-webkit-inner-spin-button': {
                    opacity: 1,
                    WebkitAppearance: 'inner-spin-button',
                  },
                  '&::-webkit-outer-spin-button': {
                    opacity: 1,
                  },
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" sx={{ fontSize: '14px', color: '#666666' }}>
                    列:
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
          <Button 
            variant="contained" 
            onClick={onApplyColumns}
            sx={{
              minWidth: '80px',
              height: '56px',
              borderRadius: '8px',
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              },
            }}
          >
            適用
          </Button>
        </Box>
      </Box>

      {/* スライド幅設定 */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000' }}>
            スライド幅
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#666666' }}>
            画面幅の0～100%(中央配置)
          </Typography>
        </Box>
            <TextField
              type="number"
              value={slideWidthInput}
              onChange={(e) => {
                const value = e.target.value;
                onSlideWidthInputChange(value);
                if (value === '') {
                  onSlideWidthChange(0);
                  return;
                }
                const numValue = parseInt(value);
                if (!isNaN(numValue)) {
                  onSlideWidthChange(numValue);
                }
              }}
              onBlur={(e) => onSlideWidthBlur(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 1 }}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#D0D0D0',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
                '&::-webkit-inner-spin-button': {
                  opacity: 1,
                  WebkitAppearance: 'inner-spin-button',
                },
                '&::-webkit-outer-spin-button': {
                  opacity: 1,
                },
              },
            },
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
      </Box>

      {/* 表示領域の幅設定 */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000' }}>
            表示領域の幅
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#666666' }}>
            ※任意
          </Typography>
        </Box>
            <TextField
              type="number"
          value={viewportWidthInput}
              onChange={(e) => {
                const value = e.target.value;
                onViewportWidthInputChange(value);
                if (value === '') {
              onViewportWidthChange(0);
                  return;
                }
                const numValue = parseInt(value);
                if (!isNaN(numValue)) {
                  onViewportWidthChange(numValue);
                }
              }}
              onBlur={(e) => onViewportWidthBlur(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 1 }}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#D0D0D0',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
                '&::-webkit-inner-spin-button': {
                  opacity: 1,
                  WebkitAppearance: 'inner-spin-button',
                },
                '&::-webkit-outer-spin-button': {
                  opacity: 1,
                },
              },
            },
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
      </Box>

      {/* 位置調整設定 */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000' }}>
            位置調整
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#666666' }}>
            +で下に、-で上に移動
          </Typography>
        </Box>
        <TextField
          value={positionAdjustmentInput}
          onChange={(e) => {
            const value = e.target.value;
            onPositionAdjustmentInputChange(value);
            
            if (value === '' || value === '-' || value === '+') {
              onPositionAdjustmentChange(0);
              return;
            }
            
            const numValue = Number(value);
            if (!isNaN(numValue)) {
              onPositionAdjustmentChange(numValue);
            }
          }}
          onBlur={onPositionAdjustmentBlur}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#D0D0D0',
              },
            },
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">px</InputAdornment>,
          }}
        />
      </Box>

      {/* ナビゲーションドット位置設定 */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000' }}>
            ナビゲーションドット位置
          </Typography>
        </Box>
        <FormControl fullWidth>
            <Select
              value={indicatorPosition}
              onChange={(e) => onIndicatorPositionChange(e.target.value as 'top' | 'bottom')}
            sx={{
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#D0D0D0',
              },
            }}
            >
              <MenuItem value="top">画像の上</MenuItem>
              <MenuItem value="bottom">画像の下</MenuItem>
            </Select>
          </FormControl>
        </Box>
        
      {/* ナビゲーションドット位置調整 */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#000000' }}>
            ナビゲーションドット位置調整
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#666666' }}>
            +で下(右)に、-で上(左)に移動
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="上下位置"
            value={indicatorOffsetYInput}
            onChange={(e) => {
              const value = e.target.value;
              onIndicatorOffsetYInputChange(value);
              
              if (value === '' || value === '-' || value === '+') {
                onIndicatorOffsetYChange(0);
                return;
              }
              
              const numValue = Number(value);
              if (!isNaN(numValue)) {
                onIndicatorOffsetYChange(numValue);
              }
            }}
            onBlur={onIndicatorOffsetYBlur}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                '& fieldset': {
                  borderColor: '#D0D0D0',
                },
              },
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">px</InputAdornment>,
            }}
          />
          <FormControl fullWidth>
            <InputLabel>左右位置</InputLabel>
            <Select
              label="左右位置"
              value={indicatorOffsetX === 0 ? 'center' : indicatorOffsetX > 0 ? 'right' : 'left'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'center') {
                  onIndicatorOffsetXChange(0);
                  onIndicatorOffsetXInputChange('');
                } else if (value === 'right') {
                  onIndicatorOffsetXChange(20);
                  onIndicatorOffsetXInputChange('20');
                } else {
                  onIndicatorOffsetXChange(-20);
                  onIndicatorOffsetXInputChange('-20');
                }
              }}
              sx={{
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                '& fieldset': {
                  borderColor: '#D0D0D0',
                },
              }}
            >
              <MenuItem value="center">中央</MenuItem>
              <MenuItem value="left">左</MenuItem>
              <MenuItem value="right">右</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* 初期表示スライド設定 */}
      <Box sx={{ mb: 3 }}>
        <TextField
          type="number"
          label="初期表示スライド"
          value={initialIndex}
          onChange={(e) => onInitialIndexChange(parseInt(e.target.value) || 1)}
          inputProps={{ min: 1, max: Math.max(horizontalSlidesLength, 1) }}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#D0D0D0',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
                '&::-webkit-inner-spin-button': {
                  opacity: 1,
                  WebkitAppearance: 'inner-spin-button',
                },
                '&::-webkit-outer-spin-button': {
                  opacity: 1,
                },
              },
            },
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">枚目</InputAdornment>,
          }}
        />
      </Box>

      {/* 無限ループ設定 */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            onClick={() => onInfiniteLoopChange(!infiniteLoop)}
            sx={{
              position: 'relative',
              width: '48px',
              height: '24px',
              backgroundColor: infiniteLoop ? '#F49121' : '#E0E0E0',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              display: 'flex',
              alignItems: 'center',
              padding: '2px',
            }}
          >
            <Box
              sx={{
                width: '20px',
                height: '20px',
                backgroundColor: '#FFFFFF',
                borderRadius: '50%',
                transform: infiniteLoop ? 'translateX(24px)' : 'translateX(0px)',
                transition: 'transform 0.3s',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000' }}>
            無限ループ: {infiniteLoop ? 'ON' : 'OFF'}
          </Typography>
        </Box>
      </Box>
    </>
  );
};
